import {Button, Drawer, Empty, Pagination, Spin} from 'antd';
import {useContext, useEffect, useMemo, useState} from 'react';
import {LuFilter} from 'react-icons/lu';
import {Link, useSearchParams} from 'react-router-dom';

import {FilterContext} from '../../context/FilterContext/FilterContext';
import {RqGetProducts} from '../../reactQuery/apis';

import ProductFilters from './ProductFilters/ProductFilters';
import SelectedFilters from './SelectedFilters/SelectedFilters';
import SortDropdown from './SortDropdown/SortDropdown';

/* Get first available variant image */
const getDefaultImage = product => {
  if (!product?.variants?.length) {return '';}
  for (const variant of product.variants) {
    if (variant.images?.length) {return variant.images[0];}
  }
  return '';
};

const ProductPage = () => {
  const [sortType, setSortType] = useState('new');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeImages, setActiveImages] = useState({});

  const {filters, setFilters, resetFilters} = useContext(FilterContext);
  const {data: allProducts = [], isLoading} = RqGetProducts();

  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const searchText = searchParams.get('search') || '';

  const [products, setProducts] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState(null);

  const filterConfig = {};
  if (selectedUser) { filterConfig.userId = selectedUser; }
  if (products) { filterConfig.productId = products; }
  if(selectedStatus) {filterConfig.status = selectedStatus;}

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;

  /* Search text filter */
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      productName: searchText,
    }));
  }, [searchText, setFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortType]);

  /* Category from URL */
  useEffect(() => {
    if (categoryFromUrl) {
      setFilters(prev => ({
        ...prev,
        productCategories: [categoryFromUrl],
      }));
    }
  }, [categoryFromUrl, setFilters]);

  /* FILTER PRODUCTS */
  const filteredProducts = useMemo(() => {
    return allProducts.filter(product => {
      // CATEGORY
      if (
        filters.productCategories.length &&
        !filters.productCategories.includes(product.category)
      ) {
        return false;
      }

      // BRAND
      if (
        filters.brands.length &&
        !filters.brands.includes(product.brand)
      ) {
        return false;
      }

      // FEATURES
      if (filters.features.length) {
        const productFeatures = Array.isArray(product.features)
          ? product.features
          : [];

        const categoryFeatures =
    product.categoryInfo?.features?.features || [];

        const combinedFeatures = [...productFeatures, ...categoryFeatures].map(
          f => f.trim().toLowerCase(),
        );

        const selectedFeatures = filters.features.map(f =>
          f.trim().toLowerCase(),
        );

        const hasAnyFeature = selectedFeatures.some(f =>
          combinedFeatures.includes(f),
        );

        if (!hasAnyFeature) {
          return false;
        }
      }

      // PRICE
      if (filters.minPrice !== '' && product.price < filters.minPrice) {return false;}
      if (filters.maxPrice !== '' && product.price > filters.maxPrice) {return false;}

      // COLORS
      if (
        filters.colors.length &&
        !product.variants?.some(v => filters.colors.includes(v.color))
      ) {
        return false;
      }
      if (
        searchText &&
  !product.productName
    ?.toLowerCase()
    .includes(searchText.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [allProducts, filters]);

  /* SORT */
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      if (sortType === 'low-high') {return a.price - b.price;}
      if (sortType === 'high-low') {return b.price - a.price;}
      return new Date(b.createdOn) - new Date(a.createdOn);
    });
  }, [filteredProducts, sortType]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedProducts.slice(start, end);
  }, [sortedProducts, currentPage]);

  const handleVariantImageChange = (productId, images) => {
    if (!images || images.length === 0) {return;}

    setActiveImages(prev => ({
      ...prev,
      [productId]: images[0],
    }));
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-40'}>
        <Spin />
      </div>
    );
  }
  const toDropboxRawUrl = (url = '') =>
    url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');
  return (
    <div className={'flex gap-6 px-2 lg:px-2 mt-5'}>
      {/* Desktop Filters */}
      <div className={'hidden lg:block w-60 bg-white rounded-xl p-4 sticky top-4 h-[80vh] overflow-y-auto'}>
        <ProductFilters filters={filters} setFilters={setFilters} products={allProducts} />
      </div>

      {/* Products */}
      <div className={'flex-1'}>
        <div className={'flex gap-4 pt-14 '}>
          <Button
            className={'lg:hidden w-32 h-12 font-semibold'}
            onClick={() => setFilterDrawerOpen(true)}
          >
            <LuFilter /> Filters
          </Button>

          <SortDropdown sortType={sortType} setSortType={setSortType} />
        </div>

        <SelectedFilters
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
        />

        <div className={'grid  grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 md:p-0 p-2 gap-4 mt-5'}
        >
          {paginatedProducts.length ? (
            paginatedProducts.map(product => {
              const img =
                activeImages[product.id] || getDefaultImage(product);
              const discountedPrice =
                Math.round(product.price - (product.price * product.discount) / 100);

              return (
                <div key={product.id} className={'bg-white rounded-xl shadow p-2'}>
                  <Link to={`/product/${product.id}`} state={{product}}>
                    <img
                      key={product.id}
                      src={toDropboxRawUrl(img)}
                      alt={`Product-${product.id}`}
                      className={'w-full h-44 md:h-80 object-cover rounded-lg'}

                    />

                    {/* Color Switcher */}
                    {product.variants?.length > 0 && (
                      <div className={'flex gap-2 mt-3 pl-2'}>
                        {product.variants.map((variant, idx) => {
                          const hasImages = variant.images?.length > 0;
                          const isActive =
        activeImages[product.id] === variant.images?.[0];

                          return (
                            <button
                              key={idx}
                              type={'button'}
                              title={variant.color}
                              disabled={!hasImages}
                              onMouseEnter={() =>
                                handleVariantImageChange(product.id, variant.images)
                              }
                              onClick={() =>
                                handleVariantImageChange(product.id, variant.images)
                              }
                              className={`w-5 h-5 rounded-full border transition-all duration-200
            ${isActive ? 'border-primary-900 scale-110' : 'border-gray-300'}
            ${!hasImages ? 'opacity-50 cursor-not-allowed' : ''}
          `}
                              style={{backgroundColor: variant.color}}
                            />
                          );
                        })}
                      </div>
                    )}

                    <h3 className={'mt-2 pl-2 text-lg font-medium'}>{product.productName}</h3>

                    <div className={'flex md:flex-row pl-2 flex-col gap-2 mb-2 mt-2'}>
                      <p className={'font-semibold md:text-xl text-lg'}>₹{discountedPrice}</p>
                      {product.discount > 0 && (
                        <>
                          <div className={'flex gap-2'}>
                            <p className={'line-through md:font-normal font-extralight text-gray'}>
                            ₹{product.price}
                            </p>
                            <span className={'bg-red-600 flex justify-center items-center text-white px-2 rounded-full md:text-sm'}>
                            -{product.discount}%
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })
          ) : (
            <div className={'col-span-full flex justify-center'}>
              <Empty />
            </div>
          )}
        </div>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={sortedProducts.length}
          onChange={page => {
            setCurrentPage(page);
            window.scrollTo({top: 0, behavior: 'smooth'});
          }}
          showSizeChanger={false}
          className={'flex justify-center my-6'}
        />
      </div>

      {/* Mobile Filters */}
      <Drawer
        title={'Filters'}
        placement={'left'}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        width={300}
        className={'lg:hidden'}
      >
        <ProductFilters filters={filters} setFilters={setFilters} products={allProducts} />
      </Drawer>
    </div>
  );
};

export default ProductPage;
